import React, { useState, useRef } from 'react';
import { Music as MusicIcon, Play, Clock, Video, Sparkles, Pause } from 'lucide-react';
import { Button } from './ui/button';
import { VideoPlayer } from './VideoPlayer';
import { VirtualConcert } from './VirtualConcert';
import { musicData, redactedTrack, redactedArtwork } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Music = () => {
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [showConcert, setShowConcert] = useState(false);
  const [concertTrack, setConcertTrack] = useState(null);
  const [promoPlaying, setPromoPlaying] = useState(false);
  const promoVideoRef = useRef(null);

  const togglePromoVideo = () => {
    if (promoVideoRef.current) {
      if (promoPlaying) {
        promoVideoRef.current.pause();
      } else {
        promoVideoRef.current.play();
      }
      setPromoPlaying(!promoPlaying);
    }
  };

  const handleTrackSelect = (track) => {
    setSelectedTrack(track);
  };

  const handleClosePlayer = () => {
    setSelectedTrack(null);
  };

  const handleEnterConcert = () => {
    // Use the special REDACTED track #56 FUNK
    setConcertTrack(redactedTrack);
    setShowConcert(true);
  };

  const handleCloseConcert = () => {
    setShowConcert(false);
    setConcertTrack(null);
  };

  const handleNext = () => {
    const currentIndex = musicData.findIndex(t => t.id === selectedTrack.id);
    const nextIndex = (currentIndex + 1) % musicData.length;
    setSelectedTrack(musicData[nextIndex]);
  };

  const handlePrevious = () => {
    const currentIndex = musicData.findIndex(t => t.id === selectedTrack.id);
    const previousIndex = currentIndex === 0 ? musicData.length - 1 : currentIndex - 1;
    setSelectedTrack(musicData[previousIndex]);
  };

  return (
    <>
      {/* Virtual Concert Experience */}
      {showConcert && (
        <VirtualConcert 
          track={concertTrack}
          onClose={handleCloseConcert}
        />
      )}

      {/* Video Player Modal */}
      {selectedTrack && (
        <VideoPlayer 
          track={selectedTrack}
          onClose={handleClosePlayer}
          onNext={handleNext}
          onPrevious={handlePrevious}
        />
      )}

      <section id="music" className="relative py-24 bg-gunmetal-950">
        {/* Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/5749200/pexels-photo-5749200.jpeg)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gunmetal-950 via-gunmetal-900/50 to-gunmetal-950"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4">
              <span className="text-platinum animate-chrome-pulse">ORIGINAL </span>
              <span className="text-accent-red">SOUNDSCAPES</span>
            </h2>
            <div className="w-24 h-1 bg-accent-red mx-auto mb-6"></div>
            <p className="text-xl text-steel-400 max-w-3xl mx-auto mb-2">
              150+ songs and soundscapes that provide the soundtrack to a life of resilience and transformation
            </p>
            <p className="text-accent-red font-semibold flex items-center justify-center space-x-2">
              <Video size={20} />
              <span>Watch full music videos with each track</span>
            </p>
          </div>

          {/* REDACTED - Virtual Concert Portal */}
          <div className="mb-16 relative group" data-testid="redacted-concert-portal">
            <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-black via-gunmetal-950 to-black">
              {/* Band Artwork Background */}
              <div className="absolute inset-0">
                <img 
                  src={redactedArtwork.bandLive} 
                  alt="THE REDACTED Live"
                  className="w-full h-full object-cover opacity-30"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
              </div>
              
              {/* Animated background elements */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-cyan-900/20 via-transparent to-transparent" />
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-accent-red/10 rounded-full blur-3xl animate-pulse" />
                <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                {/* Grid lines */}
                <div className="absolute inset-0 opacity-10" style={{
                  backgroundImage: 'linear-gradient(cyan 1px, transparent 1px), linear-gradient(90deg, cyan 1px, transparent 1px)',
                  backgroundSize: '50px 50px'
                }} />
              </div>

              <div className="relative z-10 p-8 md:p-12 flex flex-col lg:flex-row items-center justify-between gap-8">
                <div className="text-center lg:text-left">
                  <div className="flex items-center justify-center lg:justify-start space-x-2 mb-3">
                    <span className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/50 rounded-full text-cyan-400 text-xs font-bold uppercase tracking-widest">
                      Immersive 3D Experience
                    </span>
                    <span className="px-3 py-1 bg-accent-red/20 border border-accent-red/50 rounded-full text-accent-red text-xs font-bold uppercase tracking-widest">
                      THE SHINE
                    </span>
                  </div>
                  <h3 className="text-4xl md:text-5xl font-black mb-4">
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-500 to-accent-red animate-pulse">
                      THE REDACTED
                    </span>
                  </h3>
                  <p className="text-xl text-steel-300 mb-2 font-bold">
                    (The Enigma) - Enter the Virtual Concert Universe
                  </p>
                  <p className="text-steel-400 max-w-lg">
                    Experience <span className="text-accent-red font-bold">THE SHINE</span> in 5 unique venues - 
                    Obsidian Void, Neon City, Abyss, Cosmic Void, and Laser Matrix. 
                    Audio-reactive visuals, floating crystals, and pure immersion.
                  </p>
                </div>

                <div className="flex flex-col items-center space-y-4">
                  {/* Promo Video Preview */}
                  <div className="w-64 h-36 rounded-xl overflow-hidden border-2 border-cyan-500/50 shadow-lg shadow-cyan-500/20 mb-2 relative group cursor-pointer" onClick={togglePromoVideo}>
                    <video 
                      ref={promoVideoRef}
                      src={`${API_URL}/api/uploads/files/redacted_promo.mp4`}
                      className="w-full h-full object-cover"
                      loop
                      muted
                      playsInline
                      onEnded={() => setPromoPlaying(false)}
                    />
                    <div className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity ${promoPlaying ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}>
                      {promoPlaying ? (
                        <Pause className="text-white" size={40} />
                      ) : (
                        <Play className="text-white" size={40} />
                      )}
                    </div>
                    <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs text-cyan-400 font-bold">
                      PROMO VIDEO
                    </div>
                  </div>
                  <Button
                    onClick={handleEnterConcert}
                    className="group relative px-10 py-6 text-lg font-black bg-gradient-to-r from-cyan-500 via-purple-500 to-accent-red hover:from-cyan-400 hover:via-purple-400 hover:to-red-500 text-white rounded-xl transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/50"
                    data-testid="enter-concert-btn"
                  >
                    <span className="flex items-center space-x-3">
                      <Sparkles className="animate-pulse" size={24} />
                      <span>ENTER THE VOID</span>
                      <Sparkles className="animate-pulse" size={24} />
                    </span>
                    {/* Glow effect */}
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500 via-purple-500 to-accent-red opacity-0 group-hover:opacity-50 blur-xl transition-opacity duration-500" />
                  </Button>
                  <p className="text-steel-500 text-sm">Now playing: <span className="text-accent-red font-bold">THE SHINE</span></p>
                </div>
              </div>

              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyan-500/50" />
              <div className="absolute top-0 right-0 w-16 h-16 border-r-2 border-t-2 border-purple-500/50" />
              <div className="absolute bottom-0 left-0 w-16 h-16 border-l-2 border-b-2 border-accent-red/50" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-cyan-500/50" />
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start mb-16">
            {/* Left - Featured Video Preview */}
            <div className="relative group cursor-pointer" onClick={() => handleTrackSelect(musicData[0])}>
              <div className="overflow-hidden rounded-lg shadow-2xl">
                <div className="relative">
                  <img 
                    src={musicData[0].thumbnail}
                    alt="Featured Track"
                    className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-gunmetal-950 via-gunmetal-950/50 to-transparent flex items-center justify-center">
                    <div className="w-20 h-20 bg-accent-red rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-2xl shadow-accent-red/50">
                      <Play className="text-white ml-1" size={32} fill="white" />
                    </div>
                  </div>
                  <div className="absolute top-4 left-4 bg-accent-red/90 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <p className="text-white font-bold flex items-center space-x-2">
                      <Video size={20} />
                      <span>Featured Video</span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-full h-full border-2 border-accent-red rounded-lg -z-10"></div>
              <div className="mt-4">
                <h4 className="text-xl font-bold text-steel-100 group-hover:text-accent-red transition-colors duration-300">
                  {musicData[0].title}
                </h4>
                <p className="text-steel-400">{musicData[0].description}</p>
              </div>
            </div>

            {/* Right - Track List */}
            <div className="space-y-4">
              {musicData.map((track) => (
                <div 
                  key={track.id}
                  className="group bg-gunmetal-900/50 backdrop-blur-sm rounded-lg p-5 border border-steel-700/30 hover:border-accent-red/50 transition-all duration-300 hover:transform hover:translate-x-2 cursor-pointer"
                  onClick={() => handleTrackSelect(track)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      {/* Play Button with Video Icon */}
                      <div className="relative">
                        <button
                          className="w-12 h-12 flex items-center justify-center bg-accent-red rounded-full hover:bg-accent-red-dark transition-colors duration-300 hover:shadow-lg hover:shadow-accent-red/50"
                        >
                          <Play className="text-white ml-0.5" size={20} fill="white" />
                        </button>
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-steel-700 rounded-full flex items-center justify-center">
                          <Video size={12} className="text-accent-red" />
                        </div>
                      </div>

                      {/* Track Info */}
                      <div className="flex-1">
                        <h4 className="text-steel-100 font-bold group-hover:text-accent-red transition-colors duration-300">
                          {track.title}
                        </h4>
                        <p className="text-steel-500 text-sm">{track.description}</p>
                      </div>
                    </div>

                    {/* Duration & Genre */}
                    <div className="hidden sm:flex flex-col items-end space-y-1">
                      <span className="text-steel-400 flex items-center space-x-1">
                        <Clock size={14} />
                        <span className="text-sm">{track.duration}</span>
                      </span>
                      <span className="text-xs px-2 py-1 bg-gunmetal-800 text-steel-400 rounded-full">
                        {track.genre}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Music Philosophy Section */}
          <div className="bg-gradient-to-r from-gunmetal-900/50 to-gunmetal-800/50 backdrop-blur-sm rounded-lg p-8 md:p-12 border border-steel-700/30">
            <div className="flex items-start space-x-6">
              <div className="hidden md:block p-4 bg-accent-red/10 rounded-lg">
                <MusicIcon className="text-accent-red" size={48} />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-steel-100 mb-4">
                  Music as a Catalyst for Change
                </h3>
                <p className="text-steel-400 leading-relaxed mb-6">
                  Each of the 150+ compositions is crafted to resonate with your journey—whether you're seeking motivation, 
                  reflection, or simply a powerful soundtrack to your daily grind. From raw rock anthems to 
                  contemplative instrumentals, 30 years of musical storytelling bridges emotion and action. 
                  Watch full music videos with stunning visuals that bring each track to life.
                </p>
                <Button 
                  className="bg-accent-red hover:bg-accent-red-dark text-white font-bold px-6 py-3 transition-all duration-300 hover:shadow-lg hover:shadow-accent-red/30 group"
                  onClick={() => handleTrackSelect(musicData[0])}
                >
                  <Video className="mr-2 group-hover:scale-110 transition-transform duration-300" size={20} />
                  Watch Featured Video
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
